import { PusherChannelEvent } from 'gcs-common/pusher/pusher-constants';
import type { EventConfig } from 'gcs-common/pusher/pusher-context';
import { usePusherEvents } from 'gcs-common/pusher/pusher-hooks';
import { getChannelListChannelIds } from 'gcs-common/slices/channels/channelsSelectors';
import { channelUpdated } from 'gcs-common/slices/channels/channelsSlice';
import fetchCurrentUserChannels from 'gcs-common/slices/channels/channelsThunks/fetchCurrentUserChannels';
import handleChannelJoined from 'gcs-common/slices/channels/channelsThunks/handleChannelJoined';
import handleChannelLeft from 'gcs-common/slices/channels/channelsThunks/handleChannelLeft';
import handleChannelRemoved from 'gcs-common/slices/channels/channelsThunks/handleChannelRemoved';
import { getIsChatConnected } from 'gcs-common/slices/chatConnection/chatConnectionSelectors';
import { getCurrentUserId } from 'gcs-common/slices/currentUser/currentUserSelectors';
import { updateCurrentChatUser } from 'gcs-common/slices/currentUser/currentUserThunks/updateCurrentChatUser';
import { getChannelMembers, getMembers } from 'gcs-common/slices/members/membersSelectors';
import { memberUpdated } from 'gcs-common/slices/members/membersSlice';
import { handleMemberJoined, removeMember, typingStarted, updateMember } from 'gcs-common/slices/members/membersThunks';
import { messageDeleted } from 'gcs-common/slices/messages/messagesSlice';
import addMessages from 'gcs-common/slices/messages/messagesThunks/addMessages';
import updateMessage from 'gcs-common/slices/messages/messagesThunks/updateMessage';
import { userUpdated } from 'gcs-common/slices/users/usersSlice';
import type { components } from 'gcs-common/types/openapi';
import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

export function usePusherChatSubscriptions() {
  const channelIds = useSelector(getChannelListChannelIds) as string[];
  const channelMembers = useSelector(getChannelMembers) as Record<string, string[]>;
  const members = useSelector(getMembers) as Record<string, { userId: string; channelId: string }>;
  const currentUserId = useSelector(getCurrentUserId);
  const dispatch = useDispatch();
  const isChatConnected = useSelector(getIsChatConnected);

  const eventConfigs = useMemo(() => {
    if (!isChatConnected) return [];

    const configs: EventConfig[] = [];

    // personal channel for the current user to receive
    // events beyond existing channels
    configs.push({
      channelName: `presence-current-user-${currentUserId}`,
      eventName: PusherChannelEvent.CHANNEL_LIST_UPDATED,
      callback: () => {
        dispatch(fetchCurrentUserChannels());
      },
    });

    // biome-ignore lint/complexity/noForEach: <explanation>
    channelIds.forEach((channelId) => {
      if (!channelId) return;

      configs.push({
        channelName: `private-chat-channel-${channelId}`,
        eventName: PusherChannelEvent.MESSAGE_ADDED,
        callback: (message: components['schemas']['ChatMessageCreatedDTO']) => {
          dispatch(
            addMessages({
              channelMessages: { [message.channelId]: [message] },
            }),
          );

          // Reset typing status immediately when a new message is received
          const memberId = Object.keys(members).find(
            (key) => members[key]?.userId === message.authorId && members[key]?.channelId === message.channelId,
          );

          if (memberId) {
            dispatch(updateMember({ member: { id: memberId, isTyping: false } }));
          }

          if (!message.autogenerated) {
            dispatch(channelUpdated({ channel: { id: message.channelId, lastMessageDate: message.createdAt } }));
          }
        },
      });

      configs.push({
        channelName: `private-chat-channel-${channelId}`,
        eventName: PusherChannelEvent.MESSAGE_UPDATED,
        callback: (message: components['schemas']['ChatMessageCreatedDTO']) => {
          dispatch(updateMessage({ message }));
        },
      });

      configs.push({
        channelName: `private-chat-channel-${channelId}`,
        eventName: PusherChannelEvent.MESSAGE_DELETED,
        callback: (message: components['schemas']['ChatMessageCreatedDTO']) => {
          dispatch(messageDeleted({ messageId: message.id }));
        },
      });

      configs.push({
        channelName: `private-chat-channel-${channelId}`,
        eventName: PusherChannelEvent.CHANNEL_UPDATED,
        callback: (channel) => {
          dispatch(channelUpdated({ channel }));
        },
      });

      configs.push({
        channelName: `private-chat-channel-${channelId}`,
        eventName: PusherChannelEvent.CHANNEL_DELETED,
        callback: () => {
          dispatch(handleChannelRemoved({ channelId }));
        },
      });

      configs.push({
        channelName: `private-chat-channel-${channelId}`,
        eventName: PusherChannelEvent.MEMBER_ADDED,
        callback: (member: components['schemas']['ChatMemberCreatedDTO']) => {
          if (member.userId === currentUserId) {
            dispatch(handleChannelJoined({ channelId }));
          }
          dispatch(handleMemberJoined({ member }));
        },
      });

      configs.push({
        channelName: `private-chat-channel-${channelId}`,
        eventName: PusherChannelEvent.MEMBER_REMOVED,
        callback: (member: components['schemas']['ChatMemberCreatedDTO']) => {
          if (member.userId === currentUserId) {
            dispatch(handleChannelLeft({ channelId }));
          }
          dispatch(removeMember({ member }));
        },
      });

      configs.push({
        channelName: `private-chat-channel-${channelId}`,
        eventName: PusherChannelEvent.TYPING_STARTED,
        callback: (data: components['schemas']['PusherTypingEventResponseDTO']) => {
          dispatch(typingStarted({ channelId: data.channelId, userId: data.userId }));
        },
      });

      configs.push({
        channelName: `private-chat-channel-${channelId}`,
        eventName: PusherChannelEvent.MEMBER_UPDATED,
        callback: (member: components['schemas']['ChatMemberCreatedDTO']) => {
          dispatch(memberUpdated({ member }));
        },
      });

      // biome-ignore lint/complexity/noForEach: <explanation>
      channelMembers[channelId]?.forEach((memberId) => {
        if (!memberId) return;

        const userId = members[memberId]?.userId;

        if (userId) {
          configs.push({
            channelName: `private-user-${userId}`,
            eventName: PusherChannelEvent.USER_UPDATED,
            callback: (user) => {
              if (user.id === currentUserId) {
                dispatch(updateCurrentChatUser({ user }));
              }
              dispatch(userUpdated({ user }));
            },
          });
        }
      });
    });

    return configs;
  }, [channelIds, channelMembers, members, dispatch, currentUserId, isChatConnected]);

  usePusherEvents(eventConfigs);
}
